<template>
    <div class="lead-summary">
        <div v-if="loading && !summary" class="lead-summary-grid" aria-hidden="true">
            <div v-for="n in 8" :key="n" class="lead-summary-card is-skeleton">
                <el-skeleton animated :rows="2" />
            </div>
        </div>

        <template v-else-if="summary">
            <p v-if="summary.total_leads === 0" class="lead-summary-empty">No leads in this date range.</p>
            <div class="lead-summary-grid" :class="{ 'is-refreshing': loading }">
                <button type="button" class="lead-summary-card is-clickable" @click="pick(null)">
                    <span class="lead-summary-label">Total new leads</span>
                    <span class="lead-summary-value">{{ formatCount(summary.total_leads) }}</span>
                </button>

                <button type="button" class="lead-summary-card is-clickable" :class="{ 'is-active': isActive(metric('new_24h', 'New leads (24h)')) }" @click="pick(metric('new_24h', 'New leads (24h)'))">
                    <span class="lead-summary-label">New leads (24h)</span>
                    <span class="lead-summary-value">{{ formatCount(summary.new_leads_24h) }}</span>
                </button>

                <button type="button" class="lead-summary-card is-clickable" :class="{ 'is-active': isActive(metric('human_pending', 'Human contact pending')) }" @click="pick(metric('human_pending', 'Human contact pending'))">
                    <span class="lead-summary-label">Human contact pending</span>
                    <span class="lead-summary-value">{{ formatCount(summary.human_contact_pending) }}</span>
                </button>

                <button type="button" class="lead-summary-card is-clickable is-alert" :class="{ 'is-active': isActive(metric('pending_over_24h', 'Pending > 24h')) }" @click="pick(metric('pending_over_24h', 'Pending > 24h'))">
                    <span class="lead-summary-label">Pending &gt; 24h</span>
                    <span class="lead-summary-value">{{ formatCount(summary.pending_over_24h) }}</span>
                </button>

                <button type="button" class="lead-summary-card is-clickable" :class="{ 'is-active': isActive(metric('quote_pending', 'Quote pending')) }" @click="pick(metric('quote_pending', 'Quote pending'))">
                    <span class="lead-summary-label">Quote pending</span>
                    <span class="lead-summary-value">{{ formatCount(summary.quote_pending) }}</span>
                </button>

                <div class="lead-summary-card">
                    <span class="lead-summary-label">Avg. time to first call</span>
                    <span class="lead-summary-value">{{ summary.avg_time_to_first_call || '0h' }}</span>
                </div>

                <div class="lead-summary-card">
                    <span class="lead-summary-label">Total quote value</span>
                    <span class="lead-summary-value">{{ formatMoney(summary.total_quote_value) }}</span>
                </div>

                <div class="lead-summary-card lead-summary-ai">
                    <div class="lead-summary-ai-head">
                        <span class="lead-summary-label">AI Score distribution</span>
                        <div class="lead-summary-toggle" role="group" aria-label="AI score window">
                            <button type="button" :class="{ 'is-on': aiScope === 'all' }" @click="aiScope = 'all'">All</button>
                            <button type="button" :class="{ 'is-on': aiScope === '24h' }" @click="aiScope = '24h'">Last 24h</button>
                        </div>
                    </div>
                    <button
                        v-for="bucket in aiBuckets"
                        :key="bucket.key"
                        type="button"
                        class="lead-summary-ai-row"
                        :class="{ 'is-active': isActive(aiMetric(bucket)) }"
                        @click="pick(aiMetric(bucket))"
                    >
                        <span class="lead-summary-ai-name">{{ bucket.label }}</span>
                        <span class="lead-summary-ai-track"><span :style="{ width: bucket.width }"></span></span>
                        <span class="lead-summary-ai-count">{{ formatCount(bucket.count) }}</span>
                    </button>
                </div>
            </div>

            <div v-if="activeMetric" class="lead-summary-chip">
                <span>Filtered by {{ activeMetric.label }}</span>
                <button type="button" aria-label="Clear filter" @click="pick(null)">×</button>
            </div>

            <details class="lead-summary-more">
                <summary>More insights</summary>
                <div class="lead-summary-more-grid">
                    <button type="button" class="lead-summary-stat" :class="{ 'is-active': isActive(metric('updated_24h', 'Updated leads (24h)')) }" @click="pick(metric('updated_24h', 'Updated leads (24h)'))">
                        <span>Updated leads (24h)</span>
                        <strong>{{ formatCount(summary.updated_leads_24h) }}</strong>
                    </button>
                    <button type="button" class="lead-summary-stat" :class="{ 'is-active': isActive(metric('no_first_call', 'No first call yet')) }" @click="pick(metric('no_first_call', 'No first call yet'))">
                        <span>No first call yet</span>
                        <strong>{{ formatCount(summary.no_first_call) }}</strong>
                    </button>
                    <button type="button" class="lead-summary-stat" :class="{ 'is-active': isActive(metric('quotes_sent', 'Quotes sent')) }" @click="pick(metric('quotes_sent', 'Quotes sent'))">
                        <span>Quotes sent</span>
                        <strong>{{ formatCount(summary.quotes_sent) }}</strong>
                    </button>
                    <div class="lead-summary-stat">
                        <span>Avg. time to first quote</span>
                        <strong>{{ summary.avg_time_to_first_quote || '0h' }}</strong>
                    </div>
                    <div class="lead-summary-stat">
                        <span>Quote rate</span>
                        <strong>{{ formatRate(summary.quote_rate) }}</strong>
                    </div>
                </div>

                <div class="lead-summary-groups">
                    <section v-for="group in groups" :key="group.key">
                        <h4>{{ group.title }}</h4>
                        <button
                            v-for="row in group.rows"
                            :key="group.key + '-' + row.value"
                            type="button"
                            class="lead-summary-group-row"
                            :class="{ 'is-active': isActive(groupMetric(group, row)) }"
                            @click="pick(groupMetric(group, row))"
                        >
                            <span>{{ row.value }}</span>
                            <strong>{{ formatCount(row.count) }}</strong>
                        </button>
                        <p v-if="!group.rows.length" class="lead-summary-none">None in this range</p>
                    </section>
                </div>
            </details>
        </template>
    </div>
</template>

<script>
import { ElSkeleton } from 'element-plus'

export default {
    name: 'LeadReportSummary',
    components: { ElSkeleton },
    props: {
        summary: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        activeMetric: { type: Object, default: null },
    },
    data() {
        return {
            aiScope: 'all',
        }
    },
    computed: {
        aiBuckets() {
            const counts = (this.summary && this.summary.ai_score && this.summary.ai_score[this.aiScope === '24h' ? 'last_24h' : 'all']) || {}
            const rows = [
                { key: '0_49', label: '0–49', count: Number(counts['0_49'] || 0) },
                { key: '50_99', label: '50–99', count: Number(counts['50_99'] || 0) },
                { key: '100_plus', label: '100+', count: Number(counts['100_plus'] || 0) },
                { key: 'none', label: 'No score', count: Number(counts.none || 0) },
            ]
            const max = Math.max.apply(null, rows.map(function (row) { return row.count }).concat([1]))
            return rows.map(function (row) {
                return Object.assign({}, row, { width: Math.round((row.count / max) * 100) + '%' })
            })
        },
        groups() {
            const summary = this.summary || {}
            return [
                { key: 'relationship', title: 'Relationship strength', rows: summary.relationship_strength || [] },
                { key: 'next_step', title: 'Next step', rows: summary.next_step || [] },
                { key: 'lead_type', title: 'Lead type', rows: summary.lead_type || [] },
                { key: 'utm', title: 'UTM source', rows: summary.utm_source || [] },
            ]
        },
    },
    methods: {
        formatCount(value) {
            return new Intl.NumberFormat('en-IN').format(Number(value || 0))
        },
        formatMoney(value) {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 2,
            }).format(Number(value || 0))
        },
        formatRate(value) {
            const rate = Number(value || 0)
            return (Number.isInteger(rate) ? String(rate) : rate.toFixed(1)) + '%'
        },
        metric(key, label) {
            return { key: key, label: label, value: null, scope: null }
        },
        aiMetric(bucket) {
            const windowLabel = this.aiScope === '24h' ? 'Last 24h' : 'All'
            return {
                key: 'ai_score',
                label: 'AI Score ' + bucket.label + ' (' + windowLabel + ')',
                value: bucket.key,
                scope: this.aiScope === '24h' ? '24h' : 'all',
            }
        },
        groupMetric(group, row) {
            return {
                key: group.key,
                label: group.title + ': ' + row.value,
                value: row.bucket === 'others' ? '__others__' : row.value,
                scope: null,
            }
        },
        isActive(metric) {
            if (!this.activeMetric || !metric) {
                return false
            }
            return this.activeMetric.key === metric.key
                && (this.activeMetric.value || null) === (metric.value || null)
                && (this.activeMetric.scope || null) === (metric.scope || null)
        },
        pick(metric) {
            this.$emit('select', metric)
        },
    },
}
</script>

<style scoped>
.lead-summary {
    margin-bottom: 16px;
}
.lead-summary-empty {
    margin: 0 0 10px;
    color: #6b7280;
    font-size: 13px;
}
.lead-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
.lead-summary-grid.is-refreshing {
    opacity: 0.65;
}
.lead-summary-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
    margin: 0;
    padding: 14px 16px;
    text-align: left;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    font: inherit;
    color: inherit;
}
.lead-summary-card.is-clickable {
    cursor: pointer;
}
.lead-summary-card.is-clickable:hover,
.lead-summary-card.is-active,
.lead-summary-stat.is-active,
.lead-summary-group-row.is-active,
.lead-summary-ai-row.is-active {
    border-color: #1d4ed8;
    box-shadow: 0 0 0 1px #1d4ed8;
}
.lead-summary-card.is-alert .lead-summary-value {
    color: #b91c1c;
}
.lead-summary-label {
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
}
.lead-summary-value {
    color: #111827;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
}
.lead-summary-ai {
    grid-column: span 2;
    gap: 8px;
}
.lead-summary-ai-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}
.lead-summary-toggle {
    display: inline-flex;
    padding: 2px;
    background: #f3f4f6;
    border-radius: 999px;
}
.lead-summary-toggle button {
    border: 0;
    background: transparent;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    color: #4b5563;
    cursor: pointer;
}
.lead-summary-toggle button.is-on {
    background: #fff;
    color: #111827;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.lead-summary-ai-row {
    display: grid;
    grid-template-columns: 72px 1fr auto;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 4px 6px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    font: inherit;
    color: inherit;
}
.lead-summary-ai-name,
.lead-summary-ai-count {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
}
.lead-summary-ai-track {
    height: 8px;
    background: #f3f4f6;
    border-radius: 999px;
    overflow: hidden;
}
.lead-summary-ai-track span {
    display: block;
    height: 100%;
    background: #1d4ed8;
    border-radius: 999px;
}
.lead-summary-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 6px 8px 6px 12px;
    background: #eff6ff;
    color: #1e3a8a;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
}
.lead-summary-chip button {
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 999px;
    background: #dbeafe;
    color: #1e3a8a;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
}
.lead-summary-more {
    margin-top: 12px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 0 14px 4px;
}
.lead-summary-more summary {
    cursor: pointer;
    padding: 12px 0;
    font-weight: 700;
    color: #111827;
}
.lead-summary-more-grid,
.lead-summary-groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
    padding-bottom: 12px;
}
.lead-summary-stat,
.lead-summary-group-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    font: inherit;
    color: inherit;
    text-align: left;
}
button.lead-summary-stat,
.lead-summary-group-row {
    cursor: pointer;
}
.lead-summary-stat span,
.lead-summary-group-row span {
    color: #4b5563;
    font-size: 13px;
}
.lead-summary-groups section h4 {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
}
.lead-summary-groups section {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.lead-summary-none {
    margin: 0;
    color: #9ca3af;
    font-size: 13px;
}
@media (max-width: 1100px) {
    .lead-summary-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .lead-summary-ai {
        grid-column: span 2;
    }
}
@media (max-width: 640px) {
    .lead-summary-grid {
        grid-template-columns: 1fr;
    }
    .lead-summary-ai {
        grid-column: span 1;
    }
    .lead-summary-value {
        font-size: 20px;
    }
}
</style>
